/* eslint-disable prefer-template */
import { Injectable, Inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  checkValidResponse,
  logError,
  Message,
  MessageType,
  MessageFilters,
  MessageUpdate,
  MessagesResponse,
  ServiceError,
  ApiLobType,
  SortOrderType,
} from '@cigna/chcp/shared/util';

@Injectable({
  providedIn: 'root',
})
export class MessageCenterService {
  private messageCenterApi: string;
  private claimsApi: string;
  private CONSUMER_CODE = '1000';

  constructor(
    private http: HttpClient,
    @Inject('secureApi') secureApi: string,
    private datePipe: DatePipe,
  ) {
    this.messageCenterApi = `${secureApi}/mcenter`;
    this.claimsApi = `${secureApi}/claims`;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  fetchMessages(
    lobType: ApiLobType | '',
    {
      messagesType,
      sortOrder,
      filters,
      offset,
      limit,
    }: {
      messagesType: MessageType;
      sortOrder?: SortOrderType;
      filters?: MessageFilters;
      offset?: string;
      limit?: number;
    },
  ): Observable<MessagesResponse | ServiceError> {
    let params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    if (offset) {
      params = params.append('offset', offset);
    }
    if (limit) {
      params = params.append('limit', limit.toString());
    }
    if (sortOrder === 'asc') {
      params = params.append('sort', sortOrder);
    }
    if (filters?.flag?.some((v) => v.isSelected)) {
      params = params.append('flagged', 'true');
    }
    if (filters?.read?.some((v) => v.isSelected)) {
      params = params.append('unread', 'true');
    }
    if (filters?.date?.min) {
      params = params.append(
        'startDate',
        filters.date.min.valueOf().toString(),
      );
    }
    if (filters?.date?.max) {
      params = params.append('endDate', filters.date.max.valueOf().toString());
    }
    if (
      filters?.claimStatus &&
      filters.claimStatus !== 'all' &&
      filters.claimStatus !== 'reports'
    ) {
      params = params.append('status', filters.claimStatus);
    }

    if (filters?.claimStatus && filters.claimStatus === 'reports') {
      params = params.append('claimReportsOnly', 'true');
    }

    return this.http
      .get(
        `${this.messageCenterApi}${
          lobType ? '/' + lobType : ''
        }/v1/messages/${messagesType}`,
        {
          params,
        },
      )
      .pipe(
        map(checkValidResponse),
        map((res) => this.formatMessageData(res)),
        catchError(logError),
      );
  }

  private formatMessageData = (
    response: MessagesResponse,
  ): MessagesResponse => {
    try {
      return {
        ...response,
        messages: response.messages.map((message: Message) => {
          const createdDateFormatted = this.datePipe.transform(message.created);
          const createdTimeFormat = this.datePipe.transform(
            message.created,
            'mediumTime',
          );
          return {
            ...message,
            desc: message.desc.replace(
              'UX-DATE',
              `${createdDateFormatted} at ${createdTimeFormat}`,
            ),
          };
        }),
      };
    } catch (error) {
      throw new TypeError(error);
    }
  };

  fetchMessageDetails(
    lobType: ApiLobType | '',
    messagesType: MessageType,
    id: string,
  ): Observable<Message> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    return this.http
      .get(
        `${this.messageCenterApi}${
          lobType ? '/' + lobType : ''
        }/v1/messages/details/${id}`,
        {
          params,
        },
      )
      .pipe(
        map(checkValidResponse),
        map((resp) => ({
          ...resp.details,
          desc: resp.details.desc?.replace(
            'UX-DATE',
            `${this.datePipe.transform(
              resp.details.created,
            )} at ${this.datePipe.transform(
              resp.details.created,
              'mediumTime',
            )}`,
          ),
        })),
        catchError(logError),
      );
  }

  updateMessages(
    lobType: ApiLobType | '',
    update: MessageUpdate,
  ): Observable<void> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    return this.http
      .put(
        `${this.messageCenterApi}${lobType ? '/' + lobType : ''}/v1/messages`,
        update,
        {
          params,
        },
      )
      .pipe(map(checkValidResponse), catchError(logError));
  }

  deleteMessages(lobType: ApiLobType | '', ids: string[]): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .set('ids', ids.join(','));

    return this.http
      .delete(
        `${this.messageCenterApi}${lobType ? '/' + lobType : ''}/v1/messages`,
        { params },
      )
      .pipe(map(checkValidResponse), catchError(logError));
  }

  getPresignedUrl(keys: string[], lob: 'medical' | 'dental') {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    const requestBody = keys.map((key) => ({
      key,
    }));
    return this.http
      .post(
        `${this.claimsApi}/${lob}/reports/v1/get-presigned-url`,
        requestBody,
        {
          params,
        },
      )
      .pipe(
        map(checkValidResponse),
        map((res) => res.details),
        catchError(logError),
      );
  }
}
